import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert from "components/ui/Alert/Alert";
import DataTable, { type DataTableColumn } from "components/ui/DataTable/DataTable";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import { useAuth } from "context/AuthContext";
import { useI18n } from "context/I18nContext";
import { ApiError } from "utils/apiClient";
import {
  fetchWaterTransactions,
  type PaginationMeta,
  type WaterTransactionSummary,
} from "utils/waterTransactionsApi";
import { formatCurrency } from "utils/format";
import {
  formatDateTime,
  toDateInputValue,
  toRangeEndIso,
  toRangeStartIso,
} from "utils/dateUtils";
import styles from "./WaterTransactionsPage.module.scss";

const PAGE_SIZE = 10;
const DATE_FROM_PARAM = "fromDate";
const DATE_TO_PARAM = "toDate";

const extractDetailCode = (detail: unknown): string | undefined => {
  if (!detail) {
    return undefined;
  }
  if (typeof detail === "string") {
    return detail;
  }
  if (typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    const candidate = record.code ?? record.error ?? record.type;
    return typeof candidate === "string" ? candidate : undefined;
  }
  return undefined;
};

function WaterTransactionsPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const pumpList = user?.pumps ?? [];
  const hasPumps = pumpList.length > 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Math.max(1, Number(searchParams.get("page")) || 1);
  const rawSearchParam = searchParams.get("search") ?? "";
  const searchTerm = rawSearchParam.trim();
  const fromDateParam = searchParams.get(DATE_FROM_PARAM) ?? "";
  const toDateParam = searchParams.get(DATE_TO_PARAM) ?? "";
  const pumpIdParam = searchParams.get("pumpId") ?? "";

  const [transactions, setTransactions] = useState<WaterTransactionSummary[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [searchInput, setSearchInput] = useState(rawSearchParam);
  const [fromDateInput, setFromDateInput] = useState(() => toDateInputValue(fromDateParam));
  const [toDateInput, setToDateInput] = useState(() => toDateInputValue(toDateParam));
  const [pumpIdInput, setPumpIdInput] = useState(pumpIdParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchCooldownRef = useRef<number | null>(null);
  const [searchLocked, setSearchLocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const firstPumpId = pumpList[0]?.pumpId ? String(pumpList[0].pumpId) : "";
  const selectedPumpId = pumpIdInput || firstPumpId;
  const pumpFilterLabel = useMemo(() => {
    const label = t("waterTransactionsPage.filters.pump");
    return label === "waterTransactionsPage.filters.pump" ? "Pump" : label;
  }, [t]);

  useEffect(() => {
    setSearchInput(rawSearchParam);
  }, [rawSearchParam]);

  useEffect(() => {
    setFromDateInput(toDateInputValue(fromDateParam));
  }, [fromDateParam]);

  useEffect(() => {
    setToDateInput(toDateInputValue(toDateParam));
  }, [toDateParam]);

  useEffect(() => {
    const next = pumpIdParam || firstPumpId || "";
    setPumpIdInput(next);
    if (!pumpIdParam && next) {
      const params = new URLSearchParams(searchParams);
      params.set("pumpId", next);
      params.set("page", "1");
      setSearchParams(params, { replace: false });
    }
  }, [pumpIdParam, firstPumpId, searchParams, setSearchParams]);

  useEffect(
    () => () => {
      if (searchCooldownRef.current) {
        window.clearTimeout(searchCooldownRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const pumpIdNumber = selectedPumpId ? Number(selectedPumpId) : undefined;
        const resolvedPumpId = Number.isFinite(pumpIdNumber) ? pumpIdNumber : undefined;
        const response = await fetchWaterTransactions({
          page: pageParam,
          pageSize: PAGE_SIZE,
          search: searchTerm || undefined,
          fromDate: fromDateParam || undefined,
          toDate: toDateParam || undefined,
          pumpId: resolvedPumpId,
          signal: controller.signal,
        });
        setTransactions(response.items);
        setMeta(response.meta);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        const apiError = err instanceof ApiError ? err : null;
        const detailCode = extractDetailCode(apiError?.detail);
        const message =
          detailCode === "pump_access_denied"
            ? t("waterTransactionsPage.errors.pumpAccessDenied")
            : apiError?.message || t("waterTransactionsPage.error");
        setError(message);
        setTransactions([]);
        setMeta(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [pageParam, searchTerm, fromDateParam, toDateParam, t, reloadKey, selectedPumpId]);

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchLocked) {
      return;
    }
    setSearchLocked(true);
    if (searchCooldownRef.current) {
      window.clearTimeout(searchCooldownRef.current);
    }
    searchCooldownRef.current = window.setTimeout(() => setSearchLocked(false), 3000);

    const trimmed = searchInput.trim();
    const nextFromParam = toRangeStartIso(fromDateInput);
    const nextToParam = toRangeEndIso(toDateInput);
    if (
      trimmed === searchTerm &&
      (nextFromParam ?? "") === fromDateParam &&
      (nextToParam ?? "") === toDateParam &&
      (selectedPumpId || "") === pumpIdParam
    ) {
      setReloadKey((prev) => prev + 1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (trimmed) {
      next.set("search", trimmed);
    } else {
      next.delete("search");
    }
    if (nextFromParam) {
      next.set(DATE_FROM_PARAM, nextFromParam);
    } else {
      next.delete(DATE_FROM_PARAM);
    }
    if (nextToParam) {
      next.set(DATE_TO_PARAM, nextToParam);
    } else {
      next.delete(DATE_TO_PARAM);
    }
    if (selectedPumpId) {
      next.set("pumpId", selectedPumpId);
    } else {
      next.delete("pumpId");
    }
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage === (meta?.page ?? pageParam)) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: false });
  };

  const columns = useMemo<DataTableColumn<WaterTransactionSummary>[]>(
    () => [
      {
        id: "accountNo",
        header: t("waterTransactionsPage.table.account"),
        accessorKey: "accountNo",
      },
      {
        id: "pumpName",
        header: t("waterTransactionsPage.table.pump"),
        accessorKey: "pumpName",
      },
      {
        id: "zoneName",
        header: t("waterTransactionsPage.table.zone"),
        accessorKey: "zoneName",
      },
      {
        id: "cardNumber",
        header: t("waterTransactionsPage.table.card"),
        accessorKey: "cardNumber",
      },
      {
        id: "waterDispenseLtr",
        header: t("waterTransactionsPage.table.water"),
        accessorKey: "waterDispenseLtr",
        cell: (value) => <span>{Number(value ?? 0).toLocaleString()}</span>,
        align: "right",
      },
      {
        id: "unitWaterPrice",
        header: t("waterTransactionsPage.table.unitPrice"),
        accessorKey: "unitWaterPrice",
        cell: (value) => <span>{formatCurrency(Number(value ?? 0), "৳")}</span>,
        align: "right",
      },
      {
        id: "amount",
        header: t("waterTransactionsPage.table.amount"),
        accessorKey: "amount",
        cell: (value) => <span>{formatCurrency(Number(value ?? 0), "৳")}</span>,
        align: "right",
      },
      {
        id: "transactionDate",
        header: t("waterTransactionsPage.table.date"),
        accessorKey: "transactionDate",
        cell: (value) => <span>{formatDateTime(value, language)}</span>,
      },
    ],
    [language, t]
  );

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("waterTransactionsPage.title")}</h1>
          <div className={styles.subtitle}>{t("waterTransactionsPage.subtitle")}</div>
        </div>

        <form className={styles.searchBar} onSubmit={handleSearchSubmit}>
          <label className={styles.fieldGroup} htmlFor="water-search">
            <span className={styles.fieldLabel}>{t("waterTransactionsPage.searchLabel")}</span>
            <input
              id="water-search"
              className={styles.searchInput}
              type="search"
              placeholder={t("waterTransactionsPage.searchPlaceholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="water-from-date">
            <span className={styles.fieldLabel}>{t("waterTransactionsPage.filters.from")}</span>
            <input
              id="water-from-date"
              className={styles.dateInput}
              type="date"
              value={fromDateInput}
              max={toDateInput || undefined}
              onChange={(event) => setFromDateInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="water-to-date">
            <span className={styles.fieldLabel}>{t("waterTransactionsPage.filters.to")}</span>
            <input
              id="water-to-date"
              className={styles.dateInput}
              type="date"
              value={toDateInput}
              min={fromDateInput || undefined}
              onChange={(event) => setToDateInput(event.target.value)}
            />
          </label>
          {hasPumps && (
            <label className={styles.fieldGroup} htmlFor="water-pump">
              <span className={styles.fieldLabel}>{pumpFilterLabel}</span>
              <select
                id="water-pump"
                className={styles.select}
                value={selectedPumpId}
                onChange={(event) => setPumpIdInput(event.target.value)}
              >
                {pumpList.map((pump) => (
                  <option key={pump.pumpId} value={pump.pumpId}>
                    {pump.pumpName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className={styles.searchButton} disabled={searchLocked}>
            {t("datatable.search")}
          </button>
        </form>

        {error && (
          <div className={styles.alertStack}>
            <Alert variant="error" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}

        <DataTable
          columns={columns}
          data={transactions}
          globalSearch={false}
          loading={loading}
          loadingText={t("waterTransactionsPage.loading")}
          emptyState={
            loading
              ? t("waterTransactionsPage.loading")
              : error
                ? t("waterTransactionsPage.error")
                : t("waterTransactionsPage.empty")
          }
          manualPagination={
            meta
              ? {
                  pageIndex: Math.max((meta.page ?? 1) - 1, 0),
                  pageCount: Math.max(meta.pages ?? 1, 1),
                  pageSize: meta.pageSize ?? PAGE_SIZE,
                  totalItems: meta.total ?? transactions.length,
                  onPageChange: (index) => handlePageChange(index + 1),
                }
              : undefined
          }
          showPaginationControls={false}
          showPaginationInfo={false}
        />

        {meta && (
          <PaginationFooter
            totalLabel={t("waterTransactionsPage.meta.total")}
            pageLabel={t("waterTransactionsPage.meta.page")}
            pageSizeLabel={t("waterTransactionsPage.meta.pageSize")}
            total={meta.total ?? 0}
            page={meta.page ?? 1}
            pageCount={Math.max(meta.pages ?? 1, 1)}
            pageSize={meta.pageSize ?? PAGE_SIZE}
            previousLabel={t("datatable.previous")}
            nextLabel={t("datatable.next")}
            goToLabel={t("waterTransactionsPage.pagination.goToLabel")}
            goButtonLabel={t("waterTransactionsPage.pagination.goButton")}
            invalidMessage={(max) =>
              t("waterTransactionsPage.pagination.invalidPage", { max })
            }
            onPageChange={handlePageChange}
          />
        )}
      </Card>
    </div>
  );
}

export default WaterTransactionsPage;
